// convex/system/licensing/crypto.ts
//
// Firma y verificación de licencias con ECDSA P-256 + SHA-256 vía Web Crypto.
// Módulo PURO y runtime-agnóstico: funciona en el isolate de Convex, en Node
// (actions) y en el webview de Tauri. NO importa nada de Convex a propósito,
// para poder reutilizarlo embebido en el cliente on-prem.
//
// Formato del token:  base64url(payloadJSON) + "." + base64url(firma)
// La firma cubre los bytes UTF-8 del segmento base64url(payloadJSON).

export type LicensePayload = {
  /** licenseId público, ej. "lic_ab12..." */
  lid: string;
  /** nombre o identificador del cliente */
  cust: string;
  /** edición autorizada */
  ed: "saas" | "desktop" | "onprem" | "free_desktop";
  /** tipo de licencia */
  type: "lifetime" | "subscription";
  /** ids de módulos permitidos (vacío = todos los de la edición) */
  mods: string[];
  /** límite de usuarios (0 = ilimitado) */
  seats: number;
  /** límite de sucursales (0 o ausente = ilimitado) */
  mb?: number;
  /** emitida (epoch en segundos) */
  iat: number;
  /** expira (epoch en segundos) o null si lifetime */
  exp: number | null;
  /** días máximos sin heartbeat antes de degradar */
  hb: number;
};

const subtle = (): SubtleCrypto => {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) {
    throw new Error("Web Crypto (crypto.subtle) no disponible en este runtime");
  }
  return c.subtle;
};

const ECDSA_PARAMS = { name: "ECDSA", namedCurve: "P-256" } as const;
const SIGN_PARAMS = { name: "ECDSA", hash: "SHA-256" } as const;

// ── base64 / base64url ────────────────────────────────────────────────────

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  // btoa existe en isolates y navegadores; en Node 20+ también es global.
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function base64urlEncode(bytes: Uint8Array): string {
  return bytesToBase64(bytes)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function base64urlDecode(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  return base64ToBytes(b64 + pad);
}

// ── import de claves (formato DER en base64: pkcs8 / spki) ─────────────────

/** Clave privada para FIRMAR. Vive solo en tu nube (LICENSE_PRIVATE_KEY). */
export function importPrivateKey(pkcs8Base64: string): Promise<CryptoKey> {
  return subtle().importKey(
    "pkcs8",
    base64ToBytes(pkcs8Base64.trim()).buffer as ArrayBuffer,
    ECDSA_PARAMS,
    false,
    ["sign"],
  );
}

/** Clave pública para VERIFICAR. Se embebe en el build on-prem. */
export function importPublicKey(spkiBase64: string): Promise<CryptoKey> {
  return subtle().importKey(
    "spki",
    base64ToBytes(spkiBase64.trim()).buffer as ArrayBuffer,
    ECDSA_PARAMS,
    true,
    ["verify"],
  );
}

// ── firma / verificación ──────────────────────────────────────────────────

/** Firma un payload y devuelve el token compacto. */
export async function signLicense(
  payload: LicensePayload,
  privateKey: CryptoKey,
): Promise<string> {
  const enc = new TextEncoder();
  const payloadSegment = base64urlEncode(enc.encode(JSON.stringify(payload)));
  const sig = await subtle().sign(
    SIGN_PARAMS,
    privateKey,
    enc.encode(payloadSegment),
  );
  return `${payloadSegment}.${base64urlEncode(new Uint8Array(sig))}`;
}

export type VerifyResult =
  | { valid: true; payload: LicensePayload }
  | { valid: false; reason: string };

/**
 * Verifica la firma del token con la clave pública. NO valida expiración ni
 * edición — eso lo hace `evaluateLicense`. Solo confirma autenticidad.
 */
export async function verifyLicenseToken(
  token: string,
  publicKey: CryptoKey,
): Promise<VerifyResult> {
  const dot = token.indexOf(".");
  if (dot <= 0) {
    return { valid: false, reason: "formato_invalido" };
  }

  const payloadSegment = token.slice(0, dot);
  const sigSegment = token.slice(dot + 1);

  let ok: boolean;
  try {
    ok = await subtle().verify(
      SIGN_PARAMS,
      publicKey,
      base64urlDecode(sigSegment).buffer as ArrayBuffer,
      new TextEncoder().encode(payloadSegment),
    );
  } catch (e) {
    return { valid: false, reason: "firma_corrupta" };
  }
  if (!ok) {
    return { valid: false, reason: "firma_no_valida" };
  }

  try {
    const payload = JSON.parse(
      new TextDecoder().decode(base64urlDecode(payloadSegment)),
    ) as LicensePayload;
    return { valid: true, payload };
  } catch {
    return { valid: false, reason: "payload_corrupto" };
  }
}

// ── evaluación de reglas (offline) ────────────────────────────────────────

export type LicenseState =
  | "ok"
  | "expired"
  | "wrong_edition"
  | "grace_exceeded";

export type Evaluation = {
  state: LicenseState;
  /** true si la app debe operar con normalidad */
  allowed: boolean;
};

/**
 * Aplica las reglas de negocio sobre un payload ya verificado.
 * @param expectedEdition edición del build actual (ej. "onprem")
 * @param lastHeartbeatOkAt epoch s de la última validación online exitosa
 * @param now epoch s actual
 */
export function evaluateLicense(
  payload: LicensePayload,
  expectedEdition: LicensePayload["ed"],
  lastHeartbeatOkAt: number | null,
  now: number = Math.floor(Date.now() / 1000),
): Evaluation {
  if (payload.ed !== expectedEdition) {
    return { state: "wrong_edition", allowed: false };
  }
  if (payload.exp !== null && now > payload.exp) {
    return { state: "expired", allowed: false };
  }
  // La edición free (desktop local gratis) NO exige heartbeat: se valida solo la
  // firma + la edición + la expiración. No degrada por falta de validación online
  // (no hay período de gracia), así que puede correr offline indefinidamente.
  if (payload.ed === "free_desktop") {
    return { state: "ok", allowed: true };
  }
  // Heartbeat: si nunca validó, se concede el grace desde la emisión.
  const since = lastHeartbeatOkAt ?? payload.iat;
  const graceSeconds = payload.hb * 24 * 60 * 60;
  if (now - since > graceSeconds) {
    return { state: "grace_exceeded", allowed: false };
  }
  return { state: "ok", allowed: true };
}
